import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrandLogo } from '../../components/BrandLogo';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  identifyTerminal,
  listSetupPrinters,
  printBarcodeTest,
  printReceiptTest,
  type IdentifiedBranch,
} from '../../../data/api/terminalSetupApi';
import { registerPosInstallation } from '../../../data/api/posInstallationApi';
import { ensurePosDeviceId } from '../../../printing/posDeviceId';
import type { CupsPrinterInfo } from '../../../printing/thermalReceiptTypes';
import {
  resolveFrontofficeRuntime,
} from '../../../runtime/frontofficeRuntime';
import {
  TERMINAL_SETUP_CHANGED_EVENT,
  advanceFromBranch,
  advanceFromPrinters,
  branchesForSetup,
  editSection,
  persistTerminalSetup,
  type ReceiptPaperSize,
  type TerminalSetup,
  type WizardStep,
} from './terminalSetup';
import { RECEIPT_PAPER_SIZES } from '../../../printing/thermalReceiptTypes';

function paperSizeLabel(
  size: ReceiptPaperSize,
  t: (key: string) => string,
): string {
  return size === '58mm'
    ? t('terminal_setup.paper_58mm')
    : t('terminal_setup.paper_80mm');
}

function barcodeOptionLabel(
  enabled: boolean,
  t: (key: string) => string,
): string {
  return enabled
    ? t('terminal_setup.barcode_option_yes')
    : t('terminal_setup.barcode_option_no');
}

const STEPS: Array<{ id: WizardStep; labelKey: string }> = [
  { id: 1, labelKey: 'terminal_setup.progress_auth' },
  { id: 2, labelKey: 'terminal_setup.progress_store' },
  { id: 3, labelKey: 'terminal_setup.progress_devices' },
  { id: 4, labelKey: 'terminal_setup.progress_confirm' },
];

const TerminalSetupScreen: React.FC = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState<WizardStep>(1);
  const [backToConfirm, setBackToConfirm] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [setupBy, setSetupBy] = useState('');
  const [storeName, setStoreName] = useState('QalamShop');
  const [branches, setBranches] = useState<IdentifiedBranch[]>([]);
  const [branchId, setBranchId] = useState<string | null>(null);
  const [printers, setPrinters] = useState<CupsPrinterInfo[]>([]);
  const [receiptPrinter, setReceiptPrinter] = useState<string | null>(null);
  const [receiptPaperSize, setReceiptPaperSize] =
    useState<ReceiptPaperSize>('80mm');
  const [barcodePrinter, setBarcodePrinter] = useState<string | null>(null);
  const [barcodeIncludeName, setBarcodeIncludeName] = useState(true);
  const [barcodeIncludePrice, setBarcodeIncludePrice] = useState(true);
  const [receiptTest, setReceiptTest] = useState('');
  const [barcodeTest, setBarcodeTest] = useState('');

  const selectedBranch = branches.find((branch) => branch.visibleId === branchId) ?? null;

  const onIdentify = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      const result = await identifyTerminal(username.trim(), password);
      if (!result.ok) {
        setError(
          result.status === 403
            ? t('terminal_setup.cashier_rejected')
            : t('terminal_setup.bad_credentials'),
        );
        return;
      }
      const role = result.data.user.role;
      setToken(result.data.token.accessToken);
      setSetupBy(result.data.user.username);
      setStoreName(result.data.storeName);
      setBranches(branchesForSetup(role, result.data.branches));
      setStep(2);
    } catch {
      setError(t('terminal_setup.bad_credentials'));
    } finally {
      setBusy(false);
    }
  };

  const loadPrinters = async (accessToken: string) => {
    const listed = await listSetupPrinters(accessToken);
    setPrinters(listed);
  };

  const goNextFromBranch = async () => {
    if (!branchId || !token) return;
    const next = advanceFromBranch(backToConfirm);
    if (next === 3) {
      setBusy(true);
      try {
        await loadPrinters(token);
      } finally {
        setBusy(false);
      }
    }
    setBackToConfirm(false);
    setStep(next);
  };

  const goNextFromPrinters = () => {
    setBackToConfirm(false);
    setStep(advanceFromPrinters(backToConfirm));
  };

  const onEdit = async (section: 'branch' | 'printers') => {
    const target = editSection(section);
    if (section === 'printers' && token) {
      setBusy(true);
      try {
        await loadPrinters(token);
      } finally {
        setBusy(false);
      }
    }
    setBackToConfirm(target.backToConfirm);
    setStep(target.step);
  };

  const onTest = async (kind: 'receipt' | 'barcode') => {
    if (!token || !selectedBranch) return;
    const printerName = kind === 'receipt' ? receiptPrinter : barcodePrinter;
    if (!printerName) return;
    const setMessage = kind === 'receipt' ? setReceiptTest : setBarcodeTest;
    setMessage('');
    setBusy(true);
    try {
      const ok =
        kind === 'receipt'
          ? await printReceiptTest(
              token,
              printerName,
              {
                storeName,
                branchName: selectedBranch.name,
                receiptId: 'SETUP-TEST',
                createdAt: new Date().toISOString(),
                cashierName: setupBy || username,
                lines: [
                  {
                    name: 'Test',
                    quantity: 1,
                    unitPriceTtc: 0,
                    lineTotalTtc: 0,
                  },
                ],
                subtotal: 0,
                discountAmount: 0,
                grandTotal: 0,
                paymentMethod: 'CASH',
              },
              receiptPaperSize,
            )
          : await printBarcodeTest(token, printerName, {
              includeProductName: barcodeIncludeName,
              includePriceTtc: barcodeIncludePrice,
            });
      setMessage(ok ? t('terminal_setup.test_ok') : t('terminal_setup.test_failed'));
    } catch {
      setMessage(t('terminal_setup.test_failed'));
    } finally {
      setBusy(false);
    }
  };

  const onFinalize = async () => {
    if (!selectedBranch || !token) return;
    setError('');
    setBusy(true);
    const setup: TerminalSetup = {
      storeName,
      branchId: selectedBranch.visibleId,
      branchName: selectedBranch.name,
      branchAddress: selectedBranch.address ?? '',
      receiptPrinterName: receiptPrinter,
      barcodePrinterName: barcodePrinter,
      receiptPaperSize,
      barcodeIncludeName,
      barcodeIncludePrice,
      setupBy,
      setupAt: new Date().toISOString(),
    };
    try {
      const deviceId = ensurePosDeviceId();
      const registered = await registerPosInstallation(token, {
        deviceId,
        setup,
        runtime: resolveFrontofficeRuntime(),
      });
      if (!registered.ok) {
        setError(t('terminal_setup.register_failed'));
        return;
      }
      persistTerminalSetup(setup);
      setToken(null);
      window.dispatchEvent(new Event(TERMINAL_SETUP_CHANGED_EVENT));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="h-screen w-screen overflow-y-auto bg-brand-dark text-white">
      <div className="mx-auto flex min-h-full w-full max-w-3xl flex-col px-4 py-8">
        <div className="mb-6 text-center">
          <BrandLogo size="lg" inverted showTagline />
          <p className="mt-4 text-sm text-gray-400">{t('terminal_setup.title')}</p>
          <p className="text-xs text-gray-500">{t('terminal_setup.step_label', { step })}</p>
        </div>

        <ol className="mb-6 grid grid-cols-4 gap-2">
          {STEPS.map((item) => (
            <li
              key={item.id}
              className={`rounded-lg border px-2 py-2 text-center text-xs ${
                item.id === step
                  ? 'border-brand bg-brand/20 text-white'
                  : item.id < step
                    ? 'border-gray-700 text-gray-300'
                    : 'border-gray-800 text-gray-600'
              }`}
            >
              {t(item.labelKey)}
            </li>
          ))}
        </ol>

        <div className="rounded-2xl border border-gray-800 bg-gray-900 p-5 sm:p-8">
          {step === 1 && (
            <form onSubmit={onIdentify} className="space-y-4">
              <h1 className="text-lg font-semibold">{t('terminal_setup.auth_heading')}</h1>
              <p className="text-sm text-gray-400">{t('terminal_setup.auth_hint')}</p>
              <div className="space-y-1.5">
                <Label htmlFor="setup-username">{t('login.username_label')}</Label>
                <Input
                  id="setup-username"
                  data-testid="setup-username"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  placeholder={t('login.username_placeholder')}
                  autoComplete="username"
                  className="h-12 bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="setup-password">{t('login.password_label')}</Label>
                <Input
                  id="setup-password"
                  data-testid="setup-password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder={t('login.password_placeholder')}
                  autoComplete="current-password"
                  className="h-12 bg-gray-800 border-gray-700 text-white"
                />
              </div>
              {error && (
                <p className="rounded-lg border border-red-800 bg-red-950 p-3 text-sm text-red-300" role="alert">
                  {error}
                </p>
              )}
              <Button
                type="submit"
                data-testid="setup-continue"
                disabled={busy || username.trim().length < 3 || password.trim().length < 6}
                className="h-12 w-full bg-brand text-white"
              >
                {t('terminal_setup.continue')}
              </Button>
            </form>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h1 className="text-lg font-semibold">{storeName}</h1>
              <p className="text-sm text-gray-400">
                {t('terminal_setup.welcome')} {setupBy}. {t('terminal_setup.pick_branch')}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {branches.map((branch) => (
                  <button
                    key={branch.visibleId}
                    type="button"
                    data-testid="branch-card"
                    data-branch-id={branch.visibleId}
                    onClick={() => setBranchId(branch.visibleId)}
                    className={`rounded-xl border p-4 text-left ${
                      branchId === branch.visibleId
                        ? 'border-brand bg-brand/15'
                        : 'border-gray-700 bg-gray-800'
                    }`}
                  >
                    <span className="block font-semibold">{branch.name}</span>
                    <span className="mt-1 block text-sm text-gray-400">{branch.address}</span>
                  </button>
                ))}
              </div>
              <Button
                type="button"
                data-testid="setup-next"
                disabled={!branchId || busy}
                onClick={() => void goNextFromBranch()}
                className="h-12 w-full bg-brand text-white"
              >
                {backToConfirm ? t('terminal_setup.save_back') : t('terminal_setup.next')}
              </Button>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h1 className="text-lg font-semibold">{t('terminal_setup.progress_devices')}</h1>
              <p className="text-sm text-gray-400">{t('terminal_setup.devices_hint')}</p>
              <PrinterCard
                title={t('terminal_setup.receipt_printer')}
                printers={printers}
                selected={receiptPrinter}
                onSelect={setReceiptPrinter}
                noneLabel={t('terminal_setup.none')}
                testLabel={t('terminal_setup.test_print')}
                testMessage={receiptTest}
                busy={busy}
                onTest={() => void onTest('receipt')}
              />
              <fieldset className="rounded-xl border border-gray-700 bg-gray-800/60 p-4">
                <legend className="px-1 text-sm font-medium text-gray-200">
                  {t('terminal_setup.receipt_paper_size')}
                </legend>
                <div className="mt-2 flex flex-wrap gap-3">
                  {RECEIPT_PAPER_SIZES.map((size) => (
                    <label
                      key={size}
                      className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                        receiptPaperSize === size
                          ? 'border-brand bg-brand/15 text-white'
                          : 'border-gray-700 text-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="receipt-paper-size"
                        data-testid={`paper-size-${size}`}
                        checked={receiptPaperSize === size}
                        onChange={() => setReceiptPaperSize(size)}
                        className="accent-brand"
                      />
                      {paperSizeLabel(size, t)}
                    </label>
                  ))}
                </div>
              </fieldset>
              <PrinterCard
                title={t('terminal_setup.barcode_printer')}
                printers={printers}
                selected={barcodePrinter}
                onSelect={setBarcodePrinter}
                noneLabel={t('terminal_setup.none')}
                testLabel={t('terminal_setup.test_print')}
                testMessage={barcodeTest}
                busy={busy}
                onTest={() => void onTest('barcode')}
              />
              <fieldset className="rounded-xl border border-gray-700 bg-gray-800/60 p-4">
                <legend className="px-1 text-sm font-medium text-gray-200">
                  {t('terminal_setup.barcode_label_content')}
                </legend>
                <div className="mt-3 space-y-3">
                  <BarcodeContentToggle
                    label={t('terminal_setup.barcode_include_name')}
                    enabled={barcodeIncludeName}
                    onChange={setBarcodeIncludeName}
                    yesLabel={t('terminal_setup.barcode_option_yes')}
                    noLabel={t('terminal_setup.barcode_option_no')}
                    testId="barcode-include-name"
                  />
                  <BarcodeContentToggle
                    label={t('terminal_setup.barcode_include_price')}
                    enabled={barcodeIncludePrice}
                    onChange={setBarcodeIncludePrice}
                    yesLabel={t('terminal_setup.barcode_option_yes')}
                    noLabel={t('terminal_setup.barcode_option_no')}
                    testId="barcode-include-price"
                  />
                </div>
              </fieldset>
              <Button
                type="button"
                data-testid="setup-next"
                disabled={busy}
                onClick={goNextFromPrinters}
                className="h-12 w-full bg-brand text-white"
              >
                {backToConfirm ? t('terminal_setup.save_back') : t('terminal_setup.next')}
              </Button>
            </div>
          )}

          {step === 4 && selectedBranch && (
            <div className="space-y-4">
              <h1 className="text-lg font-semibold">{t('terminal_setup.confirm_heading')}</h1>
              <SummaryRow
                label={storeName}
                value={`${selectedBranch.name} — ${selectedBranch.address}`}
                onEdit={() => void onEdit('branch')}
                editLabel={t('terminal_setup.edit')}
              />
              <SummaryRow
                label={t('terminal_setup.receipt_printer')}
                value={receiptPrinter ?? t('terminal_setup.none')}
                onEdit={() => void onEdit('printers')}
                editLabel={t('terminal_setup.edit')}
              />
              <SummaryRow
                label={t('terminal_setup.receipt_paper_size')}
                value={paperSizeLabel(receiptPaperSize, t)}
                onEdit={() => void onEdit('printers')}
                editLabel={t('terminal_setup.edit')}
              />
              <SummaryRow
                label={t('terminal_setup.barcode_printer')}
                value={barcodePrinter ?? t('terminal_setup.none')}
                onEdit={() => void onEdit('printers')}
                editLabel={t('terminal_setup.edit')}
              />
              <SummaryRow
                label={t('terminal_setup.barcode_include_name')}
                value={barcodeOptionLabel(barcodeIncludeName, t)}
                onEdit={() => void onEdit('printers')}
                editLabel={t('terminal_setup.edit')}
              />
              <SummaryRow
                label={t('terminal_setup.barcode_include_price')}
                value={barcodeOptionLabel(barcodeIncludePrice, t)}
                onEdit={() => void onEdit('printers')}
                editLabel={t('terminal_setup.edit')}
              />
              {error && (
                <p className="rounded-lg border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
                  {error}
                </p>
              )}
              <Button
                type="button"
                data-testid="setup-finalize"
                disabled={busy}
                onClick={() => void onFinalize()}
                className="h-12 w-full bg-brand text-white"
              >
                {t('terminal_setup.finalize')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function PrinterCard({
  title,
  printers,
  selected,
  onSelect,
  noneLabel,
  testLabel,
  testMessage,
  busy,
  onTest,
}: {
  title: string;
  printers: CupsPrinterInfo[];
  selected: string | null;
  onSelect: (name: string | null) => void;
  noneLabel: string;
  testLabel: string;
  testMessage: string;
  busy: boolean;
  onTest: () => void;
}) {
  return (
    <fieldset className="space-y-2 rounded-xl border border-gray-700 p-4">
      <legend className="px-1 text-sm font-semibold">{title}</legend>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="radio"
          name={title}
          checked={selected === null}
          onChange={() => onSelect(null)}
        />
        {noneLabel}
      </label>
      {printers.map((printer) => (
        <label key={printer.name} className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name={title}
            checked={selected === printer.name}
            onChange={() => onSelect(printer.name)}
          />
          <span>
            {printer.name}
            <span className="ml-2 text-xs text-gray-400">({printer.status})</span>
          </span>
        </label>
      ))}
      <Button
        type="button"
        variant="outline"
        disabled={busy || !selected}
        onClick={onTest}
        className="mt-2"
      >
        {testLabel}
      </Button>
      {testMessage && <p className="text-xs text-gray-400">{testMessage}</p>}
    </fieldset>
  );
}

function BarcodeContentToggle({
  label,
  enabled,
  onChange,
  yesLabel,
  noLabel,
  testId,
}: {
  label: string;
  enabled: boolean;
  onChange: (value: boolean) => void;
  yesLabel: string;
  noLabel: string;
  testId: string;
}) {
  return (
    <div>
      <p className="mb-2 text-sm text-gray-300">{label}</p>
      <div className="flex flex-wrap gap-3">
        <label
          className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
            enabled
              ? 'border-brand bg-brand/15 text-white'
              : 'border-gray-700 text-gray-300'
          }`}
        >
          <input
            type="radio"
            name={testId}
            data-testid={`${testId}-yes`}
            checked={enabled}
            onChange={() => onChange(true)}
            className="accent-brand"
          />
          {yesLabel}
        </label>
        <label
          className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
            !enabled
              ? 'border-brand bg-brand/15 text-white'
              : 'border-gray-700 text-gray-300'
          }`}
        >
          <input
            type="radio"
            name={testId}
            data-testid={`${testId}-no`}
            checked={!enabled}
            onChange={() => onChange(false)}
            className="accent-brand"
          />
          {noLabel}
        </label>
      </div>
    </div>
  );
}

function SummaryRow({
  label,
  value,
  onEdit,
  editLabel,
}: {
  label: string;
  value: string;
  onEdit: () => void;
  editLabel: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-xl border border-gray-700 p-4">
      <div>
        <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
        <p className="mt-1 text-sm">{value}</p>
      </div>
      <button type="button" onClick={onEdit} className="text-sm text-brand">
        {editLabel}
      </button>
    </div>
  );
}

export default TerminalSetupScreen;
